import { useState } from 'react';
import { MessageCircle } from 'lucide-react';
import { CIRCUITO_LANDING, CIRCUITO_WA_E164 } from '../../content/circuitoLandingCopy';
import { trackPublicEvent } from '../../lib/publicAnalytics';

function buildWaUrl(values: Record<string, string>) {
  let msg: string = CIRCUITO_LANDING.form.waTemplate;
  for (const [key, val] of Object.entries(values)) {
    msg = msg.replace(`{{${key}}}`, val.trim() || '—');
  }
  return `https://wa.me/${CIRCUITO_WA_E164}?text=${encodeURIComponent(msg)}`;
}

const inputClass =
  'mt-1.5 w-full rounded-xl border border-lunins-border bg-lunins-cream/60 px-4 py-2.5 text-sm text-lunins-ink placeholder:text-lunins-muted focus:border-lunins-accent/50 focus:outline-none focus:ring-1 focus:ring-lunins-accent/30';

const labelClass = 'block text-xs font-medium text-lunins-muted';

export default function CircuitoLeadForm() {
  const f = CIRCUITO_LANDING.form.fields;
  const [orgao, setOrgao] = useState<string>(f.orgaoOptions[0]);
  const [orgaoOutro, setOrgaoOutro] = useState('');
  const [municipio, setMunicipio] = useState('');
  const [unidades, setUnidades] = useState('');
  const [servidores, setServidores] = useState('');
  const [turnos, setTurnos] = useState('');
  const [periodo, setPeriodo] = useState('');
  const [objetivo, setObjetivo] = useState<string>(f.objetivoOptions[3]);

  const orgaoFinal = orgao === 'Outro' && orgaoOutro.trim() ? orgaoOutro.trim() : orgao;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const url = buildWaUrl({
      orgao: orgaoFinal,
      municipio,
      unidades,
      servidores,
      turnos,
      periodo,
      objetivo,
    });
    trackPublicEvent('circuito_whatsapp_click', { orgao: orgaoFinal, objetivo });
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-3xl border border-lunins-border bg-lunins-surface p-6 md:p-8"
    >
      <div className="grid gap-5 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label htmlFor="circuito-orgao" className={labelClass}>
            {f.orgao}
          </label>
          <select
            id="circuito-orgao"
            value={orgao}
            onChange={(e) => setOrgao(e.target.value)}
            className={inputClass}
          >
            {f.orgaoOptions.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
          {orgao === 'Outro' && (
            <input
              type="text"
              placeholder="Especifique o órgão"
              value={orgaoOutro}
              onChange={(e) => setOrgaoOutro(e.target.value)}
              className={`${inputClass} mt-2`}
            />
          )}
        </div>

        <div>
          <label htmlFor="circuito-municipio" className={labelClass}>
            {f.municipio}
          </label>
          <input
            id="circuito-municipio"
            type="text"
            required
            placeholder="Ex.: Campo Grande / MS"
            value={municipio}
            onChange={(e) => setMunicipio(e.target.value)}
            className={inputClass}
          />
        </div>

        <div>
          <label htmlFor="circuito-unidades" className={labelClass}>
            {f.unidades}
          </label>
          <input
            id="circuito-unidades"
            type="text"
            required
            placeholder="Ex.: 25"
            value={unidades}
            onChange={(e) => setUnidades(e.target.value)}
            className={inputClass}
          />
        </div>

        <div>
          <label htmlFor="circuito-servidores" className={labelClass}>
            {f.servidores}
          </label>
          <input
            id="circuito-servidores"
            type="text"
            placeholder="Ex.: 800"
            value={servidores}
            onChange={(e) => setServidores(e.target.value)}
            className={inputClass}
          />
        </div>

        <div>
          <label htmlFor="circuito-turnos" className={labelClass}>
            {f.turnos}
          </label>
          <input
            id="circuito-turnos"
            type="text"
            placeholder="Ex.: manhã, tarde, plantão 12h"
            value={turnos}
            onChange={(e) => setTurnos(e.target.value)}
            className={inputClass}
          />
        </div>

        <div>
          <label htmlFor="circuito-periodo" className={labelClass}>
            {f.periodo}
          </label>
          <input
            id="circuito-periodo"
            type="text"
            placeholder="Ex.: Setembro 2026 ou 2 semanas"
            value={periodo}
            onChange={(e) => setPeriodo(e.target.value)}
            className={inputClass}
          />
        </div>

        <div className="sm:col-span-2">
          <label htmlFor="circuito-objetivo" className={labelClass}>
            {f.objetivo}
          </label>
          <select
            id="circuito-objetivo"
            value={objetivo}
            onChange={(e) => setObjetivo(e.target.value)}
            className={inputClass}
          >
            {f.objetivoOptions.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </div>
      </div>

      <button
        type="submit"
        className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-emerald-500 px-6 py-3.5 text-sm font-bold text-lunins-ink shadow-lg shadow-emerald-900/25 transition hover:bg-emerald-400 sm:w-auto"
      >
        <MessageCircle className="h-4 w-4" />
        {CIRCUITO_LANDING.form.submit}
      </button>
    </form>
  );
}
